import { useState, useEffect, useMemo, useCallback } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Pencil,
  Trash2,
  Play,
  Square,
  RefreshCw,
  Search,
  Sparkles,
  AlertCircle,
} from "lucide-react";

interface ATCFeed {
  id: string;
  airport_code: string;
  airport_name: string;
  city: string;
  country: string;
  stream_url: string;
  frequency: string | null;
  feed_type: string;
  is_active: boolean;
  updated_at: string;
}

const FEED_TYPES = ["tower", "approach", "ground", "departure", "mixed", "Control"];

/**
 * Convert LiveATC player-page URLs into the actual audio stream URL.
 *
 * Examples handled:
 *   https://www.liveatc.net/hlisten.php?mount=cyyz1_dep_128800&icao=cyyz
 *     → https://d.liveatc.net/cyyz1_dep_128800
 *   https://www.liveatc.net/play/kjfk_twr.pls   (left as-is — handled by PLS extractor)
 *   https://d.liveatc.net/kjfk_twr               (already a stream — left as-is)
 */
function normalizeStreamUrl(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;

  try {
    const u = new URL(trimmed);
    const host = u.hostname.toLowerCase();

    // hlisten.php?mount=<mount>  →  https://d.liveatc.net/<mount>
    if (host.endsWith("liveatc.net") && /\/hlisten\.php$/i.test(u.pathname)) {
      const mount = u.searchParams.get("mount");
      if (mount) {
        return `https://d.liveatc.net/${mount.trim()}`;
      }
    }

    // listen.php?mount=<mount> (older variant)
    if (host.endsWith("liveatc.net") && /\/listen\.php$/i.test(u.pathname)) {
      const mount = u.searchParams.get("mount");
      if (mount) {
        return `https://d.liveatc.net/${mount.trim()}`;
      }
    }
  } catch {
    /* not a parseable URL — let validation surface the error */
  }

  return trimmed;
}

const feedSchema = z.object({
  airport_code: z
    .string()
    .trim()
    .regex(/^[A-Z0-9]{3,4}$/, "ICAO code must be 3-4 uppercase letters/numbers (e.g. KJFK)"),
  airport_name: z.string().trim().min(1, "Required").max(120),
  city: z.string().trim().min(1, "Required").max(80),
  country: z.string().trim().min(1, "Required").max(80),
  stream_url: z
    .string()
    .trim()
    .url("Must be a valid URL")
    .refine(
      (u) => /^https?:\/\//i.test(u),
      "URL must start with http:// or https://"
    ),
  frequency: z.string().trim().max(20).optional().or(z.literal("")),
  feed_type: z.string().trim().min(1, "Required"),
  is_active: z.boolean(),
});

type FeedFormValues = z.infer<typeof feedSchema>;

const emptyForm: FeedFormValues = {
  airport_code: "",
  airport_name: "",
  city: "",
  country: "",
  stream_url: "",
  frequency: "",
  feed_type: "tower",
  is_active: true,
};

export const ATCFeedManager = () => {
  const { toast } = useToast();
  const [feeds, setFeeds] = useState<ATCFeed[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showOnlyInactive, setShowOnlyInactive] = useState(false);

  const [editingFeed, setEditingFeed] = useState<ATCFeed | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [form, setForm] = useState<FeedFormValues>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FeedFormValues, string>>>({});
  const [saving, setSaving] = useState(false);

  // PLS extractor
  const [plsUrl, setPlsUrl] = useState("");
  const [extracting, setExtracting] = useState(false);

  // Test playback
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewAudio, setPreviewAudio] = useState<HTMLAudioElement | null>(null);

  const [verifying, setVerifying] = useState(false);

  const fetchFeeds = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("atc_feeds")
      .select("*")
      .order("airport_code", { ascending: true });

    if (error) {
      toast({
        title: "Failed to load feeds",
        description: error.message,
        variant: "destructive",
      });
    } else {
      setFeeds((data ?? []) as ATCFeed[]);
    }
    setLoading(false);
  }, [toast]);

  useEffect(() => {
    fetchFeeds();
  }, [fetchFeeds]);

  // Stop preview when component unmounts
  useEffect(() => {
    return () => {
      if (previewAudio) {
        previewAudio.pause();
        previewAudio.src = "";
      }
    };
  }, [previewAudio]);

  const filteredFeeds = useMemo(() => {
    const q = search.trim().toLowerCase();
    return feeds.filter((f) => {
      if (showOnlyInactive && f.is_active) return false;
      if (!q) return true;
      return (
        f.airport_code.toLowerCase().includes(q) ||
        f.airport_name.toLowerCase().includes(q) ||
        f.city.toLowerCase().includes(q) ||
        f.country.toLowerCase().includes(q) ||
        f.stream_url.toLowerCase().includes(q)
      );
    });
  }, [feeds, search, showOnlyInactive]);

  const counts = useMemo(
    () => ({
      total: feeds.length,
      active: feeds.filter((f) => f.is_active).length,
      inactive: feeds.filter((f) => !f.is_active).length,
    }),
    [feeds]
  );

  const openAddDialog = () => {
    setEditingFeed(null);
    setForm(emptyForm);
    setErrors({});
    setPlsUrl("");
    setIsDialogOpen(true);
  };

  const openEditDialog = (feed: ATCFeed) => {
    setEditingFeed(feed);
    setForm({
      airport_code: feed.airport_code,
      airport_name: feed.airport_name,
      city: feed.city,
      country: feed.country,
      stream_url: feed.stream_url,
      frequency: feed.frequency ?? "",
      feed_type: feed.feed_type,
      is_active: feed.is_active,
    });
    setErrors({});
    setPlsUrl("");
    setIsDialogOpen(true);
  };

  const updateField = <K extends keyof FeedFormValues>(
    key: K,
    value: FeedFormValues[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleExtractFromPLS = async () => {
    const trimmed = plsUrl.trim();
    if (!trimmed) {
      toast({ title: "Paste a PLS URL first", variant: "destructive" });
      return;
    }
    if (!/^https?:\/\/.*\.pls(\?.*)?$/i.test(trimmed)) {
      toast({
        title: "That doesn't look like a .pls URL",
        description: "Expected something like https://www.liveatc.net/play/kjfk_twr.pls",
        variant: "destructive",
      });
      return;
    }

    setExtracting(true);
    try {
      // Fetched from the admin's browser — bypasses Cloudflare bot protection
      // because the user's session/cookies are sent.
      const res = await fetch(trimmed, { credentials: "omit" });
      if (!res.ok) {
        throw new Error(`PLS fetch returned HTTP ${res.status}`);
      }
      const text = await res.text();
      const match = text.match(/File\d+\s*=\s*(.+)/i);
      const streamUrl = match?.[1]?.trim();

      if (!streamUrl || !/^https?:\/\//i.test(streamUrl)) {
        throw new Error("No File1=… line found in the playlist");
      }

      // Try to infer ICAO from the .pls filename (e.g. /play/kjfk_twr.pls -> KJFK)
      const slugMatch = trimmed.match(/\/play\/([a-z0-9]+)/i);
      const slug = slugMatch?.[1] ?? "";
      const icaoGuess = slug.match(/^([a-z]{4})/i)?.[1]?.toUpperCase() ?? "";

      // Infer feed type from slug suffix
      let typeGuess = form.feed_type;
      if (/_twr$/i.test(slug)) typeGuess = "tower";
      else if (/_app$/i.test(slug) || /_dep$/i.test(slug)) typeGuess = "approach";
      else if (/_gnd$/i.test(slug)) typeGuess = "ground";

      setForm((prev) => ({
        ...prev,
        stream_url: streamUrl,
        airport_code: prev.airport_code || icaoGuess,
        feed_type: typeGuess,
      }));

      toast({
        title: "Stream URL extracted",
        description: streamUrl,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      toast({
        title: "Couldn't extract stream URL",
        description: `${msg}. If LiveATC blocks the request, open the .pls in a new tab, copy the File1= line, and paste it into Stream URL manually.`,
        variant: "destructive",
      });
    } finally {
      setExtracting(false);
    }
  };

  const handleSave = async () => {
    // Auto-normalize any LiveATC player-page URL the admin pasted before validation.
    const normalizedUrl = normalizeStreamUrl(form.stream_url);
    const formToValidate = { ...form, stream_url: normalizedUrl };

    const parsed = feedSchema.safeParse(formToValidate);
    if (!parsed.success) {
      const next: Partial<Record<keyof FeedFormValues, string>> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof FeedFormValues;
        if (!next[key]) next[key] = issue.message;
      }
      setErrors(next);
      return;
    }

    // Reflect the normalized URL back into the form so the admin sees what was saved.
    if (normalizedUrl !== form.stream_url) {
      setForm((prev) => ({ ...prev, stream_url: normalizedUrl }));
    }

    setSaving(true);
    const payload = {
      airport_code: parsed.data.airport_code.toUpperCase(),
      airport_name: parsed.data.airport_name,
      city: parsed.data.city,
      country: parsed.data.country,
      stream_url: parsed.data.stream_url,
      feed_type: parsed.data.feed_type,
      is_active: parsed.data.is_active,
      frequency: parsed.data.frequency?.length ? parsed.data.frequency : null,
    };

    let error;
    if (editingFeed) {
      ({ error } = await supabase
        .from("atc_feeds")
        .update(payload)
        .eq("id", editingFeed.id));
    } else {
      ({ error } = await supabase.from("atc_feeds").insert([payload]));
    }

    setSaving(false);

    if (error) {
      toast({
        title: editingFeed ? "Failed to update feed" : "Failed to add feed",
        description: error.message.includes("atc_feeds_stream_url_key")
          ? "A feed with this stream URL already exists."
          : error.message,
        variant: "destructive",
      });
      return;
    }

    toast({
      title: editingFeed ? "Feed updated" : "Feed added",
      description: `${payload.airport_code} — ${payload.airport_name}`,
    });
    setIsDialogOpen(false);
    fetchFeeds();
  };

  const handleDelete = async (feed: ATCFeed) => {
    if (
      !window.confirm(
        `Delete ${feed.airport_code} — ${feed.airport_name}?\n\nThis can't be undone.`
      )
    ) {
      return;
    }
    const { error } = await supabase.from("atc_feeds").delete().eq("id", feed.id);
    if (error) {
      toast({
        title: "Failed to delete",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    toast({ title: "Feed deleted", description: feed.airport_code });
    fetchFeeds();
  };

  const handleToggleActive = async (feed: ATCFeed) => {
    const { error } = await supabase
      .from("atc_feeds")
      .update({ is_active: !feed.is_active })
      .eq("id", feed.id);
    if (error) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
      return;
    }
    setFeeds((prev) =>
      prev.map((f) => (f.id === feed.id ? { ...f, is_active: !feed.is_active } : f))
    );
  };

  const handlePreview = (feed: ATCFeed) => {
    if (previewUrl === feed.stream_url) {
      previewAudio?.pause();
      setPreviewAudio(null);
      setPreviewUrl(null);
      return;
    }
    if (previewAudio) {
      previewAudio.pause();
      previewAudio.src = "";
    }

    const audio = new Audio(feed.stream_url);
    audio.volume = 0.5;
    audio.addEventListener("error", () => {
      toast({
        title: `Preview failed: ${feed.airport_code}`,
        description: "Stream URL is unreachable from your browser.",
        variant: "destructive",
      });
      setPreviewAudio(null);
      setPreviewUrl(null);
    });
    audio.addEventListener("playing", () => {
      toast({ title: `Playing ${feed.airport_code}`, description: "Stream is live." });
    });
    audio.play().catch(() => {
      toast({
        title: `Preview blocked: ${feed.airport_code}`,
        description: "Browser blocked playback or stream is unreachable.",
        variant: "destructive",
      });
    });
    setPreviewAudio(audio);
    setPreviewUrl(feed.stream_url);
  };

  const handleVerifyAll = async () => {
    setVerifying(true);
    toast({
      title: "Verification started",
      description:
        "This runs in the background and may take 1-3 minutes. Refresh the list afterward.",
    });
    try {
      // Fire-and-forget; the function takes longer than a normal HTTP timeout.
      supabase.functions.invoke("verify-atc-streams", { body: {} }).catch(() => {
        /* ignore — function will keep running server-side */
      });
      // Give it a moment, then refresh
      setTimeout(() => {
        fetchFeeds();
        setVerifying(false);
      }, 2000);
    } catch (err) {
      setVerifying(false);
      const msg = err instanceof Error ? err.message : "Unknown error";
      toast({ title: "Verification failed to start", description: msg, variant: "destructive" });
    }
  };

  return (
    <Card className="glass-card p-8">
      <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold mb-2">ATC Feeds</h2>
            <p className="text-muted-foreground text-sm">
              Manage LiveATC stream URLs. Edit stale URLs, add new airports, or paste a{" "}
              <code className="text-xs">.pls</code> playlist URL to auto-extract the stream.
            </p>
            <div className="flex gap-3 mt-3 text-xs text-muted-foreground">
              <span>Total: <strong className="text-foreground">{counts.total}</strong></span>
              <span>Active: <strong className="text-green-500">{counts.active}</strong></span>
              <span>Inactive: <strong className="text-red-500">{counts.inactive}</strong></span>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={handleVerifyAll}
              disabled={verifying}
            >
              <RefreshCw className={`h-4 w-4 ${verifying ? "animate-spin" : ""}`} />
              Re-verify all
            </Button>
            <Button size="sm" onClick={openAddDialog}>
              <Plus className="h-4 w-4" />
              Add feed
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search ICAO, name, city, URL..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="inactive-only"
              checked={showOnlyInactive}
              onCheckedChange={setShowOnlyInactive}
            />
            <Label htmlFor="inactive-only" className="text-sm cursor-pointer">
              Only inactive
            </Label>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading feeds...</p>
        ) : filteredFeeds.length === 0 ? (
          <p className="text-sm text-muted-foreground">No feeds match.</p>
        ) : (
          <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
            {filteredFeeds.map((feed) => (
              <div
                key={feed.id}
                className="p-3 bg-white/5 rounded-lg flex flex-wrap gap-3 items-center justify-between border border-white/5 hover:border-white/10 transition-colors"
              >
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`h-2 w-2 rounded-full ${
                        feed.is_active ? "bg-green-500" : "bg-red-500"
                      }`}
                      aria-label={feed.is_active ? "Active" : "Inactive"}
                    />
                    <strong className="text-sm">{feed.airport_code}</strong>
                    <span className="text-sm text-muted-foreground">
                      {feed.airport_name}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded bg-white/10 text-muted-foreground">
                      {feed.feed_type}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 break-all font-mono">
                    {feed.stream_url}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {feed.city}, {feed.country}
                    {feed.frequency ? ` • ${feed.frequency}` : ""}
                  </p>
                </div>
                <div className="flex gap-1.5 items-center">
                  <Switch
                    checked={feed.is_active}
                    onCheckedChange={() => handleToggleActive(feed)}
                    aria-label="Toggle active"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePreview(feed)}
                    title="Preview in your browser"
                  >
                    {previewUrl === feed.stream_url ? (
                      <Square className="h-4 w-4" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openEditDialog(feed)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(feed)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingFeed ? "Edit ATC feed" : "Add ATC feed"}</DialogTitle>
            <DialogDescription>
              Provide the airport details and a working stream URL.
            </DialogDescription>
          </DialogHeader>

          {/* PLS extractor helper */}
          <div className="rounded-lg border border-dashed border-white/15 p-3 space-y-2 bg-white/[0.02]">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <Label className="text-sm">Quick add from a LiveATC playlist URL</Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Paste a <code>.pls</code> URL (e.g.{" "}
              <code>https://www.liveatc.net/play/kjfk_twr.pls</code>) and we'll extract the
              real stream URL automatically.
            </p>
            <div className="flex gap-2">
              <Input
                value={plsUrl}
                onChange={(e) => setPlsUrl(e.target.value)}
                placeholder="https://www.liveatc.net/play/<feed>.pls"
                className="text-xs font-mono"
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleExtractFromPLS}
                disabled={extracting}
              >
                {extracting ? "Extracting..." : "Extract"}
              </Button>
            </div>
            <div className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
              <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
              <span>
                If LiveATC blocks the request, open the .pls in a new browser tab, copy the
                <code className="mx-1">File1=…</code>line, and paste that URL into the Stream
                URL field below.
              </span>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2 space-y-1.5">
              <Label htmlFor="stream_url">Stream URL *</Label>
              <Input
                id="stream_url"
                value={form.stream_url}
                onChange={(e) => updateField("stream_url", e.target.value)}
                onBlur={(e) => {
                  const normalized = normalizeStreamUrl(e.target.value);
                  if (normalized !== e.target.value) {
                    updateField("stream_url", normalized);
                    toast({
                      title: "Stream URL normalized",
                      description: `Converted player-page link to direct stream: ${normalized}`,
                    });
                  }
                }}
                placeholder="https://d.liveatc.net/kjfk_twr"
                className="font-mono text-xs"
              />
              <p className="text-[11px] text-muted-foreground">
                Tip: you can paste a LiveATC player-page link like{" "}
                <code>hlisten.php?mount=…</code> and we'll convert it to the direct stream
                URL automatically.
              </p>
              {errors.stream_url && (
                <p className="text-xs text-destructive">{errors.stream_url}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="airport_code">ICAO code *</Label>
              <Input
                id="airport_code"
                value={form.airport_code}
                onChange={(e) =>
                  updateField("airport_code", e.target.value.toUpperCase())
                }
                placeholder="KJFK"
                maxLength={4}
              />
              {errors.airport_code && (
                <p className="text-xs text-destructive">{errors.airport_code}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="feed_type">Feed type *</Label>
              <Select
                value={form.feed_type}
                onValueChange={(v) => updateField("feed_type", v)}
              >
                <SelectTrigger id="feed_type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FEED_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.feed_type && (
                <p className="text-xs text-destructive">{errors.feed_type}</p>
              )}
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label htmlFor="airport_name">Airport name *</Label>
              <Input
                id="airport_name"
                value={form.airport_name}
                onChange={(e) => updateField("airport_name", e.target.value)}
                placeholder="John F. Kennedy International"
              />
              {errors.airport_name && (
                <p className="text-xs text-destructive">{errors.airport_name}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="city">City *</Label>
              <Input
                id="city"
                value={form.city}
                onChange={(e) => updateField("city", e.target.value)}
                placeholder="New York"
              />
              {errors.city && <p className="text-xs text-destructive">{errors.city}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="country">Country *</Label>
              <Input
                id="country"
                value={form.country}
                onChange={(e) => updateField("country", e.target.value)}
                placeholder="USA"
              />
              {errors.country && (
                <p className="text-xs text-destructive">{errors.country}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="frequency">Frequency (optional)</Label>
              <Input
                id="frequency"
                value={form.frequency ?? ""}
                onChange={(e) => updateField("frequency", e.target.value)}
                placeholder="119.1"
              />
              {errors.frequency && (
                <p className="text-xs text-destructive">{errors.frequency}</p>
              )}
            </div>
            <div className="flex items-center gap-2 sm:col-span-2 pt-2">
              <Switch
                id="is_active"
                checked={form.is_active}
                onCheckedChange={(v) => updateField("is_active", v)}
              />
              <Label htmlFor="is_active" className="cursor-pointer">
                Active (show to listeners)
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : editingFeed ? "Save changes" : "Add feed"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
